import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, MenuItem, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';
import KenButton from '../../../../../../GlobalComponents/KenButton';
import KenInput from '../../../../../../GlobalComponents/KenInput';
import { createFacultyActivity, FacultyCourse, NewActivityInput } from './FacultyActivities';

interface NewActivityDialogProps {
    open: boolean;
    courses: FacultyCourse[];
    defaultCourseOfferingId?: string;
    onClose: () => void;
    onCreated: () => void;
}

const emptyForm = (defaultCourseOfferingId?: string): NewActivityInput => ({
    title: '',
    courseOfferingId: defaultCourseOfferingId || '',
    brief: '',
    dueDate: '',
    file: undefined,
});

const NewActivityDialog: React.FC<NewActivityDialogProps> = ({ open, courses, defaultCourseOfferingId, onClose, onCreated }) => {
    const [form, setForm] = useState(emptyForm(defaultCourseOfferingId));
    const [saving, setSaving] = useState(false);
    // Training the Batch dropdown is narrowed to — batch numbers (e.g.
    // "Batch 1") repeat across trainings, so picking the training first
    // avoids posting an activity to the wrong same-numbered batch.
    const [selectedCourseName, setSelectedCourseName] = useState('');

    // Resolve the default batch's training whenever the dialog opens (or
    // the course list/default batch changes) so the cascade starts
    // pre-filtered to the batch it was opened for.
    useEffect(() => {
        if (!open) return;
        const defaultCourse = courses.find((c) => c.CourseOfferingID === defaultCourseOfferingId);
        setSelectedCourseName(defaultCourse?.CourseName || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultCourseOfferingId, courses]);

    // Distinct trainings across every batch the faculty teaches.
    const courseNames = useMemo(
        () => Array.from(new Set(courses.map((c) => c.CourseName).filter(Boolean))),
        [courses]
    );

    // The Batch dropdown only offers batches for the chosen Training,
    // sorted ascending (numeric-aware) so "Batch 2" comes before "Batch 10".
    const coursesForSelectedCourseName = useMemo(
        () => courses
            .filter((c) => c.CourseName === selectedCourseName)
            .sort((a, b) => a.TermName.localeCompare(b.TermName, undefined, { numeric: true, sensitivity: 'base' })),
        [courses, selectedCourseName]
    );

    // Switching Training resets the batch pick to that training's first
    // batch, so the dropdown never keeps a stale pick from a different one.
    useEffect(() => {
        if (!selectedCourseName) return;
        if (!coursesForSelectedCourseName.some((c) => c.CourseOfferingID === form.courseOfferingId)) {
            setForm((prev) => ({ ...prev, courseOfferingId: coursesForSelectedCourseName[0]?.CourseOfferingID || '' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coursesForSelectedCourseName]);

    const resetAndClose = () => {
        setForm(emptyForm(defaultCourseOfferingId));
        onClose();
    };

    const handleCreate = async () => {
        if (!form.title.trim()) {
            enqueueSnackbar('Give the activity a title before creating it.', { variant: 'warning' });
            return;
        }
        if (!form.courseOfferingId) {
            enqueueSnackbar('Select a batch for this activity.', { variant: 'warning' });
            return;
        }
        try {
            setSaving(true);
            await createFacultyActivity(form, courses);
            enqueueSnackbar(`"${form.title}" is now open for submissions.`, { variant: 'success' });
            onCreated();
            resetAndClose();
        } catch (error) {
            enqueueSnackbar('Unable to create this activity right now.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px' }}>
                <DialogTitle sx={{ padding: 0, fontSize: '16px', fontWeight: 600 }}>New activity</DialogTitle>
                <IconButton onClick={resetAndClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ paddingTop: '4px' }}>
                <Box sx={{ marginBottom: '16px' }}>
                    <KenInput
                        label="Title"
                        placeholder="e.g. Weld bead practice log"
                        fullWidth
                        value={form.title}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <Box sx={{ flex: 1 }}>
                        <KenInput
                            label="Training"
                            select
                            fullWidth
                            value={selectedCourseName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedCourseName(e.target.value)}
                        >
                            {courseNames.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </KenInput>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <KenInput
                            label="Batch"
                            select
                            fullWidth
                            disabled={!selectedCourseName}
                            value={form.courseOfferingId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, courseOfferingId: e.target.value }))}
                        >
                            {coursesForSelectedCourseName.map((course) => (
                                <MenuItem key={course.CourseOfferingID} value={course.CourseOfferingID}>
                                    {course.TermName}
                                </MenuItem>
                            ))}
                        </KenInput>
                    </Box>
                </Box>
                <Box sx={{ marginBottom: '16px' }}>
                    <KenInput
                        label="Due date"
                        type="date"
                        fullWidth
                        value={form.dueDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                    />
                </Box>
                <Box sx={{ marginBottom: '16px' }}>
                    <KenInput
                        label="Brief"
                        placeholder="Explain what the trainee should do and what to upload..."
                        multiline
                        rows={3}
                        fullWidth
                        value={form.brief}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, brief: e.target.value }))}
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: '#4A5568' }}>
                        Reference document (optional)
                    </Typography>
                    <Box
                        component="label"
                        sx={{
                            height: '42px',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 14px',
                            borderRadius: '12px',
                            backgroundColor: '#F5F7FB',
                            border: '1px solid #E5EAF2',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#263246',
                        }}
                    >
                        <Typography component="span" sx={{ flex: 1, fontSize: '14px', color: form.file ? '#1B2559' : '#8A94A4' }}>
                            {form.file ? form.file.name : 'Choose file'}
                        </Typography>
                        {form.file && (
                            <Typography
                                component="span"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setForm((prev) => ({ ...prev, file: undefined }));
                                }}
                                sx={{ fontSize: '12px', color: '#8A94A4', marginLeft: '10px' }}
                            >
                                Remove
                            </Typography>
                        )}
                        <input
                            type="file"
                            hidden
                            onChange={(e) => setForm((prev) => ({ ...prev, file: e.target.files?.[0] ?? undefined }))}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 20px' }}>
                <KenButton variant="outlined" sx={{ textTransform: 'none', borderRadius: '10px' }} onClick={resetAndClose}>
                    Cancel
                </KenButton>
                <KenButton variant="contained" disabled={saving} sx={{ textTransform: 'none', borderRadius: '10px' }} onClick={handleCreate}>
                    {saving ? 'Creating…' : 'Create activity'}
                </KenButton>
            </DialogActions>
        </Dialog>
    );
};

export default NewActivityDialog;
